import {
  BOX_CATEGORIES,
  BOX_CATEGORY_INFO,
  type BoxCategory,
  boxCategoryChildren,
  sortBoxCategories,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { StorefrontSection } from "../components/StorefrontSection";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/** The chips the boxes page shows for these categories: the ones a listed box is in, or none. */
function boxesPageChips(on: readonly BoxCategory[], counts: ReadonlyMap<BoxCategory, number>) {
  const shown = on.filter((code) => (counts.get(code) ?? 0) > 0);
  // The storefront leaves the row out unless it would offer two or more.
  return shown.length >= 2 ? shown : [];
}

function forgeSummary(on: readonly BoxCategory[]): string {
  if (on.length === BOX_CATEGORIES.length) {
    return "Creators can build with any card whose game is on the list, plus Mystery Bulk Cards.";
  }
  if (on.length === 0) return "Creators can only use Mystery Bulk Cards.";
  const n = on.length;
  return `Creators can build with cards from ${n} ${n === 1 ? "category" : "categories"}, plus Mystery Bulk Cards.`;
}

/**
 * The box categories this environment offers: the boxes page's row of chips, and the only ones
 * the forge builds with. Categories come from one fixed list (games like Pokemon and Baseball,
 * families like Sports that cover their games), and a box is in one when an item of it names
 * that game, so nothing is assigned here. One table in the list's order, each family's games
 * under it and boxed's digital lines last, with every switch editing a draft; beside it, what
 * shoppers will see once it is saved. Every category is on until an editor saves a choice. A
 * whole replace, no optimistic lock: the last save wins.
 */
export function StorefrontCategoriesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfigQuery();
  const coverage = useQuery(trpc.storefront.boxCategoryCoverage.queryOptions());

  const save = useMutation(
    trpc.storefront.setBoxCategories.mutationOptions({
      onSuccess: (saved) => {
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success(
          saved.categories.length === BOX_CATEGORIES.length
            ? "Every category is on."
            : saved.categories.length === 0
              ? "Every category is off."
              : "Categories saved.",
        );
      },
    }),
  );

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, environment } = query.data;

  return (
    <StorefrontSection
      title="Categories"
      description="Group the boxes page by game, and choose which games forge creators can build with and live breaks sell. A box is in a category when one of its items is, so there is nothing to tag."
      environment={environment}
    >
      {coverage.error ? (
        <ErrorAlert message={coverage.error.message} />
      ) : !coverage.data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <CategoriesEditor
          key={config.boxCategories.join(",")}
          counts={new Map(coverage.data.categories.map((row) => [row.category, row.boxCount]))}
          saved={config.boxCategories}
          canEdit={can("editor")}
          saving={save.isPending}
          onSave={(categories) => save.mutate({ categories })}
        />
      )}
    </StorefrontSection>
  );
}

/** The table and the summary beside it; remount with `key` when the saved value changes. */
function CategoriesEditor({
  counts,
  saved,
  canEdit,
  saving,
  onSave,
}: {
  counts: ReadonlyMap<BoxCategory, number>;
  saved: readonly BoxCategory[];
  canEdit: boolean;
  saving: boolean;
  onSave: (categories: BoxCategory[]) => void;
}) {
  const [draft, setDraft] = useState<ReadonlySet<BoxCategory>>(() => new Set(saved));
  const on = sortBoxCategories(draft);
  const dirty = on.length !== saved.length || on.some((code, i) => code !== saved[i]);
  const chips = boxesPageChips(on, counts);
  const firstDigital = BOX_CATEGORIES.find((code) => BOX_CATEGORY_INFO[code].digital);

  // A family's switch carries its games with it: off turns every game under it off (and the
  // games' switches stay off until it is back on), on turns them all on to start from.
  const toggle = (code: BoxCategory, checked: boolean) =>
    setDraft((current) => {
      const next = new Set(current);
      for (const each of [code, ...boxCategoryChildren(code)]) {
        if (checked) next.add(each);
        else next.delete(each);
      }
      return next;
    });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] lg:items-start">
      <Card>
        <CardHeader>
          <CardTitle>Categories</CardTitle>
          <CardDescription>
            A category that is on shows as a chip on the boxes page while a box is in it, and its
            cards can go in forge boxes. A family's switch turns the games under it on or off with
            it.
          </CardDescription>
          {canEdit && (
            <CardAction className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={on.length === BOX_CATEGORIES.length}
                onClick={() => setDraft(new Set(BOX_CATEGORIES))}
              >
                All on
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={on.length === 0}
                onClick={() => setDraft(new Set())}
              >
                All off
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="w-28 text-right">Boxes</TableHead>
                <TableHead className="w-20 text-right">On</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {BOX_CATEGORIES.map((code) => {
                const info = BOX_CATEGORY_INFO[code];
                const count = counts.get(code) ?? 0;
                const games = boxCategoryChildren(code).length;
                // A game can only be on while its family is.
                const familyOff = info.parent !== null && !draft.has(info.parent);
                return (
                  <Fragment key={code}>
                    {code === firstDigital && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          colSpan={3}
                          className="pt-6 text-xs font-medium tracking-wide text-muted-foreground uppercase"
                        >
                          Digital
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>
                        {/* A game sits under its family's row, a guide line marking the group. */}
                        <label
                          htmlFor={`box-category-${code}`}
                          className={
                            info.parent
                              ? "ml-2 grid gap-0.5 border-l border-border py-0.5 pl-5"
                              : "grid gap-0.5"
                          }
                        >
                          <span
                            className={
                              games > 0
                                ? "font-medium"
                                : familyOff
                                  ? "text-muted-foreground"
                                  : undefined
                            }
                          >
                            {info.label}
                          </span>
                          {games > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {draft.has(code)
                                ? `Turns the ${games} games below on and off`
                                : `Off, with the ${games} games below`}
                            </span>
                          )}
                        </label>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {count > 0 ? count : <span className="text-muted-foreground">–</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          id={`box-category-${code}`}
                          aria-label={info.label}
                          checked={draft.has(code)}
                          disabled={!canEdit || familyOff}
                          onCheckedChange={(checked) => toggle(code, checked)}
                        />
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="lg:sticky lg:top-4">
        <CardHeader>
          <CardTitle>What shoppers see</CardTitle>
          <CardDescription>
            {dirty ? "With your unsaved changes." : "With what is saved now."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div className="grid gap-2">
            <h3 className="text-sm font-medium">Boxes page</h3>
            {chips.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="secondary">All categories</Badge>
                {chips.map((code) => (
                  <Badge key={code} variant="outline">
                    {BOX_CATEGORY_INFO[code].label}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No category row. It shows once two categories that are on each hold a box.
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <h3 className="text-sm font-medium">Forge</h3>
            <p className="text-sm text-muted-foreground">{forgeSummary(on)}</p>
          </div>
        </CardContent>
        {canEdit && (
          <CardFooter className="gap-2">
            <Button disabled={saving || !dirty} onClick={() => onSave(on)}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
            {dirty && (
              <Button variant="ghost" disabled={saving} onClick={() => setDraft(new Set(saved))}>
                Discard
              </Button>
            )}
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
